import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { ArrowLeft, Upload, Link as LinkIcon, FileText, Loader2, CheckCircle2, MessageCircle, ExternalLink, Sparkles, ShieldAlert, RefreshCcw, HandHelping, X, Send, Trash2, CalendarClock, Phone, Video, Download, FileUp } from "lucide-react";
import { uploadBankerFile } from "@/lib/banker-upload";
import ProgressLoader from "@/components/progress-loader";

// Resolution intent constraint — must mirror the server-side validator in
// banker-portal.ts. Single line, 30–280 chars, no bullets. The point isn't
// length policing for its own sake — it forces the banker to compress the
// resolution into one sentence the investor can read in 4 seconds. Anything
// longer ends up unread, which means closure that doesn't actually close.
const RESOLUTION_MIN = 40;
const RESOLUTION_MAX = 280;
const DISMISS_NOTE_MIN = 20;
const DISMISS_NOTE_MAX = 500;
function validateResolutionLocal(s: string): string | null {
  const t = s.trim();
  if (t.length < RESOLUTION_MIN) return `Add ${RESOLUTION_MIN - t.length} more character${RESOLUTION_MIN - t.length === 1 ? "" : "s"} — keep it short but specific.`;
  if (t.length > RESOLUTION_MAX) return `Trim ${t.length - RESOLUTION_MAX} character${t.length - RESOLUTION_MAX === 1 ? "" : "s"} — one sentence only.`;
  if (/\r|\n/.test(t)) return "One sentence only — remove line breaks.";
  if (/(^|\s)[-•*]\s/.test(t)) return "Write a sentence, not a bullet list.";
  return null;
}

const DISMISS_REASONS: { value: string; label: string; help: string }[] = [
  { value: "false_positive", label: "False positive", help: "Meera created this from a chat that wasn't actually a request." },
  { value: "wrong_company", label: "Wrong company", help: "The portco context is mistaken — this isn't about this company." },
  { value: "duplicate", label: "Duplicate", help: "Already covered by another open task." },
  { value: "kavya_already_answered", label: "Kavya already answered", help: "The investor got their answer in chat — no banker action needed." },
  { value: "not_actionable", label: "Not actionable", help: "Vague or hypothetical — nothing concrete the desk can do." },
];
import { WheelhouseBadge } from "../../components/wheelhouse-badge";

const BASE = import.meta.env.BASE_URL;

export default function BankerTaskDetail({ taskId }: { taskId: number }) {
  const [, navigate] = useLocation();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ kind: "zoom_transcript", title: "", url: "", content: "" });
  const [uploadTab, setUploadTab] = useState<"file" | "link">("file");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [prepping, setPrepping] = useState(false);
  const [prepError, setPrepError] = useState<string | null>(null);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqReason, setReqReason] = useState("");
  const [reqSaving, setReqSaving] = useState(false);
  const [reqError, setReqError] = useState<string | null>(null);
  const [reqOk, setReqOk] = useState(false);
  // Resolve & notify investor (closes the reverse loop on the task).
  // Structured fields (S5): three short inputs replace the free-text minimum.
  // Each has its own per-field bar — together they force a real summary
  // without rewarding rambling. Concatenated server-side into one sentence.
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveWhatChanged, setResolveWhatChanged] = useState("");
  const [resolveWhatToKnow, setResolveWhatToKnow] = useState("");
  const [resolveNextStep, setResolveNextStep] = useState("");
  const [resolveSilent, setResolveSilent] = useState(false); // opt out of investor notification
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  // Dismiss flow — distinct from "close". Dismissing is for tasks that
  // never should have existed (Meera mistakes, duplicates, etc).
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState<string>("");
  const [dismissNote, setDismissNote] = useState("");
  const [dismissing, setDismissing] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);
  // Call execution primitive (T004). The banker writes a meeting link + time
  // and the investor sees it on their dashboard with .ics download. This is
  // intentionally separate from "resolve" — scheduling a call is not closing
  // the loop, it's the first half of a closure that completes when the call
  // happens and the banker writes a one-line resolution note.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedLink, setSchedLink] = useState("");
  const [schedTime, setSchedTime] = useState(""); // local datetime-input string
  const [schedNote, setSchedNote] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [schedError, setSchedError] = useState<string | null>(null);
  // Auto-create flag: when true the server mints a Google Meet via the
  // service account instead of trusting whatever URL the banker pastes.
  // Default true because it's strictly better — recordings, .ics invites,
  // and Workspace branding all come along — but the manual path stays as
  // a fallback for bankers who aren't in the favcy.in Workspace.
  const [autoCreateMeet, setAutoCreateMeet] = useState(true);
  // Phase 2: scheduling mode. 'confirmed' (legacy single-slot path that mints
  // the Meet immediately) vs 'propose' (1-3 slots, sends Kavya email, investor
  // accepts via magic link). Default 'confirmed' keeps the existing UX intact
  // for one-click bankers; 'propose' is opt-in.
  const [schedMode, setSchedMode] = useState<"confirmed" | "propose">("confirmed");
  // Up to 3 proposed slots, ISO local strings consumed by datetime-local input.
  const [proposeSlots, setProposeSlots] = useState<string[]>([""]);
  const [proposeDuration, setProposeDuration] = useState(60);

  // Investor outreach — completes the comm loop. Advisor opens the composer,
  // Kavya pre-fills a draft, advisor edits, hits Send. If banker.outreachApproval
  // is 'direct' the message goes straight out; otherwise it lands in the admin
  // approval queue. Investor replies thread back here as a banker notification.
  const [outreach, setOutreach] = useState<any[]>([]);
  const [outreachOpen, setOutreachOpen] = useState(false);
  const [outreachActive, setOutreachActive] = useState<any | null>(null);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [outreachError, setOutreachError] = useState<string | null>(null);
  const [outreachIntent, setOutreachIntent] = useState("");
  const [outreachSubject, setOutreachSubject] = useState("");
  const [outreachBody, setOutreachBody] = useState("");
  const [outreachChannel, setOutreachChannel] = useState<"both" | "in_portal" | "email">("both");
  const [outreachSending, setOutreachSending] = useState(false);

  const load = async () => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    try {
      const r = await fetch(`${BASE}api/banker/tasks/${taskId}`, { headers: { "x-banker-token": token } });
      if (r.ok) setData(await r.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [taskId]);

  const loadOutreach = async () => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    try {
      const r = await fetch(`${BASE}api/banker/outreach?taskId=${taskId}`, { headers: { "x-banker-token": token } });
      if (r.ok) setOutreach(await r.json());
    } catch {}
  };
  useEffect(() => { loadOutreach(); }, [taskId]);

  // Per-artifact suggestion lists (auto-extracted next-actions). Map artifactId
  // → array of suggestions. Loaded after task data arrives so we know which
  // artifacts to query. Honesty-over-hype: never block the page on these — if
  // the extractor hasn't run or returned nothing, the section just stays empty.
  const [suggestionsByArtifact, setSuggestionsByArtifact] = useState<Record<number, any[]>>({});
  const [suggestionPending, setSuggestionPending] = useState<Record<number, boolean>>({});
  const [suggestionEditor, setSuggestionEditor] = useState<Record<number, { title: string; priority: string; dueDays: number }>>({});
  const [processingArtifact, setProcessingArtifact] = useState<Record<number, boolean>>({});
  const loadSuggestionsForArtifact = async (artifactId: number) => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    try {
      const r = await fetch(`${BASE}api/banker/artifacts/${artifactId}/suggestions`, { headers: { "x-banker-token": token } });
      if (r.ok) {
        const j = await r.json();
        setSuggestionsByArtifact(prev => ({ ...prev, [artifactId]: j.items || [] }));
      }
    } catch {}
  };
  useEffect(() => {
    const arts = data?.artifacts;
    if (!Array.isArray(arts)) return;
    arts.forEach((a: any) => loadSuggestionsForArtifact(a.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.artifacts?.length]);

  const acceptSuggestion = async (s: any) => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    setSuggestionPending(p => ({ ...p, [s.id]: true }));
    try {
      const editor = suggestionEditor[s.id];
      const body = editor ? { title: editor.title, priority: editor.priority, dueDays: editor.dueDays } : {};
      const r = await fetch(`${BASE}api/banker/artifact-suggestions/${s.id}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        await loadSuggestionsForArtifact(s.artifactId);
        setSuggestionEditor(e => { const n = { ...e }; delete n[s.id]; return n; });
      }
    } finally {
      setSuggestionPending(p => ({ ...p, [s.id]: false }));
    }
  };
  const dismissSuggestion = async (s: any) => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    setSuggestionPending(p => ({ ...p, [s.id]: true }));
    try {
      const r = await fetch(`${BASE}api/banker/artifact-suggestions/${s.id}/dismiss`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token },
        body: JSON.stringify({}),
      });
      if (r.ok) await loadSuggestionsForArtifact(s.artifactId);
    } finally {
      setSuggestionPending(p => ({ ...p, [s.id]: false }));
    }
  };
  const processArtifact = async (artifactId: number) => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    setProcessingArtifact(p => ({ ...p, [artifactId]: true }));
    try {
      const r = await fetch(`${BASE}api/banker/artifacts/${artifactId}/process`, {
        method: "POST",
        headers: { "x-banker-token": token },
      });
      if (r.ok) await loadSuggestionsForArtifact(artifactId);
    } finally {
      setProcessingArtifact(p => ({ ...p, [artifactId]: false }));
    }
  };

  const openComposer = async (existing?: any) => {
    setOutreachError(null);
    if (existing) {
      setOutreachActive(existing);
      setOutreachSubject(existing.subject);
      // Only pre-fill the body for editable drafts. For sent / replied / no_reply
      // threads the textarea is the *reply* box, so it must start empty —
      // otherwise the advisor accidentally re-sends the original outreach.
      const isEditableDraft = existing.status === "draft" || existing.status === "rejected";
      setOutreachBody(isEditableDraft ? existing.body : "");
      setOutreachChannel((existing.channel || "both") as any);
      setOutreachIntent(existing.freeformIntent || "");
      setOutreachOpen(true);
      return;
    }
    if (!data?.task?.investorId) { setOutreachError("This task has no investor attached."); return; }
    setOutreachLoading(true); setOutreachOpen(true);
    setOutreachActive(null); setOutreachSubject(""); setOutreachBody(""); setOutreachIntent("");
    const token = localStorage.getItem("banker_token");
    try {
      const r = await fetch(`${BASE}api/banker/outreach/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
        body: JSON.stringify({ investorId: data.task.investorId, taskId }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); setOutreachError(e.error || "Couldn't draft right now"); }
      else {
        const j = await r.json();
        const o = j.outreach;
        setOutreachActive(o); setOutreachSubject(o.subject); setOutreachBody(o.body); setOutreachChannel(o.channel || "both");
      }
    } catch { setOutreachError("Network error"); }
    setOutreachLoading(false);
  };

  const saveOutreachDraft = async () => {
    if (!outreachActive) return;
    const token = localStorage.getItem("banker_token");
    try {
      const r = await fetch(`${BASE}api/banker/outreach/${outreachActive.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
        body: JSON.stringify({ subject: outreachSubject, body: outreachBody, channel: outreachChannel }),
      });
      if (r.ok) { const j = await r.json(); setOutreachActive(j.outreach); await loadOutreach(); }
    } catch {}
  };

  const sendOutreach = async () => {
    if (!outreachActive) return;
    setOutreachSending(true); setOutreachError(null);
    await saveOutreachDraft();
    const token = localStorage.getItem("banker_token");
    try {
      const r = await fetch(`${BASE}api/banker/outreach/${outreachActive.id}/send`, {
        method: "POST", headers: { "x-banker-token": token || "" },
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); setOutreachError(e.error || "Send failed"); }
      else {
        const j = await r.json();
        setOutreachActive(j.outreach);
        await loadOutreach();
        if (j.requiredApproval) setOutreachError(null);
        setTimeout(() => { setOutreachOpen(false); }, 1200);
      }
    } catch { setOutreachError("Network error"); }
    setOutreachSending(false);
  };

  const runVirenPrep = async () => {
    setPrepping(true); setPrepError(null);
    const token = localStorage.getItem("banker_token");
    try {
      const r = await fetch(`${BASE}api/viren/prep-questions/${taskId}`, { method: "POST", headers: { "x-banker-token": token || "" } });
      if (!r.ok) { const e = await r.json().catch(() => ({})); setPrepError(e.error || "Viren is unavailable"); }
      else { await load(); }
    } catch { setPrepError("Network error"); }
    setPrepping(false);
  };

  const setStatus = async (status: string) => {
    setSavingStatus(true);
    const token = localStorage.getItem("banker_token");
    try {
      await fetch(`${BASE}api/banker/tasks/${taskId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
        body: JSON.stringify({ status }),
      });
      await load();
    } catch {}
    setSavingStatus(false);
  };

  const submitResolve = async () => {
    setResolving(true); setResolveError(null);
    const token = localStorage.getItem("banker_token") || "";
    try {
      const wc = resolveWhatChanged.trim();
      const wk = resolveWhatToKnow.trim();
      const ns = resolveNextStep.trim();
      const body: any = {
        status: "closed",
        notifyInvestor: !resolveSilent,
      };
      // Only attach structured fields if we're actually notifying — silent
      // closes don't need the per-field checks.
      if (!resolveSilent && data?.task?.investorId) {
        body.resolutionFields = { whatChanged: wc, whatToKnow: wk, ...(ns ? { nextStep: ns } : {}) };
      } else if (wc || wk) {
        body.resolutionNote = [wc, wk, ns].filter(Boolean).join(" — ") || null;
      }
      const r = await fetch(`${BASE}api/banker/tasks/${taskId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "x-banker-token": token },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        setResolveOpen(false);
        setResolveWhatChanged(""); setResolveWhatToKnow(""); setResolveNextStep("");
        setResolveSilent(false);
        await load();
      } else {
        const e = await r.json().catch(() => ({}));
        setResolveError(e.error || "Could not close task");
      }
    } catch { setResolveError("Network error"); }
    setResolving(false);
  };

  const submitSchedule = async () => {
    setScheduling(true); setSchedError(null);
    const token = localStorage.getItem("banker_token");
    try {
      if (schedMode === "propose") {
        // Validate 1-3 slots, all in the future, distinct.
        const cleaned = proposeSlots.map(s => s.trim()).filter(Boolean);
        if (cleaned.length < 1) { setSchedError("Add at least one slot."); setScheduling(false); return; }
        const slots = cleaned.map(s => {
          const start = new Date(s);
          if (isNaN(start.getTime())) throw new Error("Invalid slot time");
          return {
            startUtc: start.toISOString(),
            endUtc: new Date(start.getTime() + proposeDuration * 60_000).toISOString(),
          };
        });
        const r = await fetch(`${BASE}api/banker/tasks/${taskId}/propose-call`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
          body: JSON.stringify({ slots, agenda: schedNote.trim() || undefined }),
        });
        if (r.ok) {
          setScheduleOpen(false);
          setSchedLink(""); setSchedTime(""); setSchedNote("");
          setProposeSlots([""]); setProposeDuration(60);
          setSchedMode("confirmed");
          setAutoCreateMeet(true);
          await load();
        } else {
          const e = await r.json().catch(() => ({}));
          setSchedError(e.error || "Could not send the proposal.");
        }
        setScheduling(false);
        return;
      }
      // datetime-local strings are interpreted in the browser's TZ. Build a
      // Date and ship as ISO so the server stores UTC unambiguously.
      const when = new Date(schedTime);
      if (!schedTime || isNaN(when.getTime())) {
        setSchedError("Pick a valid date and time."); setScheduling(false); return;
      }
      // When auto-creating, the server mints the Meet URL — don't ship the
      // pasted link (the server validates it strictly and would reject "" too).
      const body: Record<string, unknown> = {
        scheduledFor: when.toISOString(),
        meetingNote: schedNote.trim() || undefined,
      };
      if (autoCreateMeet) {
        body.autoCreateMeet = true;
      } else {
        body.meetingLink = schedLink.trim();
      }
      const r = await fetch(`${BASE}api/banker/tasks/${taskId}/schedule-call`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        setScheduleOpen(false);
        setSchedLink(""); setSchedTime(""); setSchedNote("");
        setAutoCreateMeet(true);
        await load();
      } else {
        const e = await r.json().catch(() => ({}));
        setSchedError(e.error || "Could not schedule the call.");
      }
    } catch (e: any) { setSchedError(e?.message || "Network error"); }
    setScheduling(false);
  };

  const tryDismissResolve = () => {
    if (resolving) return;
    const dirty = (resolveWhatChanged + resolveWhatToKnow + resolveNextStep).trim().length > 0;
    if (dirty && !window.confirm("Discard the resolution you've written?")) return;
    setResolveOpen(false);
  };

  const submitDismiss = async () => {
    setDismissing(true); setDismissError(null);
    const token = localStorage.getItem("banker_token") || "";
    try {
      const r = await fetch(`${BASE}api/banker/tasks/${taskId}/dismiss`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token },
        body: JSON.stringify({ reason: dismissReason, note: dismissNote.trim() || undefined }),
      });
      if (r.ok) {
        setDismissOpen(false);
        setDismissReason(""); setDismissNote("");
        await load();
      } else {
        const e = await r.json().catch(() => ({}));
        setDismissError(e.error || "Could not dismiss task");
      }
    } catch { setDismissError("Network error"); }
    setDismissing(false);
  };

  const handleUpload = async () => {
    if (!uploadForm.title) { setUploadError("Title is required"); return; }
    if (uploadTab === "file" && !uploadFile) { setUploadError("Choose a file to upload"); return; }
    if (uploadTab === "link" && !uploadForm.url && !uploadForm.content) {
      setUploadError("Paste a URL or content");
      return;
    }
    setUploadError(null); setUploading(true); setUploadProgress(0);
    const token = localStorage.getItem("banker_token");
    try {
      let filePayload: { fileUrl?: string; fileName?: string; fileSize?: number; mimeType?: string } = {};
      if (uploadTab === "file" && uploadFile) {
        const r = await uploadBankerFile(uploadFile, (loaded, total) => {
          setUploadProgress(Math.min(99, Math.round((loaded / total) * 100)));
        });
        filePayload = r;
      }
      const r = await fetch(`${BASE}api/banker/artifacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
        body: JSON.stringify({
          taskId, companyId: data?.task?.companyId || null,
          kind: uploadForm.kind, title: uploadForm.title,
          url: uploadTab === "link" ? (uploadForm.url || null) : null,
          content: uploadTab === "link" ? (uploadForm.content || null) : null,
          ...filePayload,
        }),
      });
      if (r.ok) {
        setUploadOpen(false);
        setUploadForm({ kind: "zoom_transcript", title: "", url: "", content: "" });
        setUploadFile(null); setUploadProgress(0);
        await load();
      } else {
        const e = await r.json();
        setUploadError(e.error || "Upload failed");
      }
    } catch (e: any) { setUploadError(e?.message || "Network error"); }
    setUploading(false);
  };

  const submitReallocation = async () => {
    if (!data?.task?.companyId) return;
    setReqSaving(true); setReqError(null);
    const token = localStorage.getItem("banker_token") || "";
    try {
      const r = await fetch(`${BASE}api/banker/me/reallocation-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token },
        body: JSON.stringify({
          companyId: data.task.companyId,
          taskId: data.task.id,
          kind: "request_to_cover",
          reason: reqReason || null,
        }),
      });
      if (r.ok) { setReqOk(true); setTimeout(() => { setReqOpen(false); setReqOk(false); setReqReason(""); }, 1200); }
      else { const e = await r.json(); setReqError(e.error || "Could not submit"); }
    } catch { setReqError("Network error"); }
    setReqSaving(false);
  };

  if (loading) return <div className="text-white/40">Loading...</div>;
  if (!data) return <div className="text-white/40">Task not found</div>;
  const { task, investor, company, artifacts, ownable, ownerBanker, viren, callRecording } = data;
  const wheelhouseStatus: "in_wheelhouse" | "other_portco" | "general" =
    !task.companyId ? "general" : ownable ? "in_wheelhouse" : "other_portco";
  const prepQuestions = (() => { try { return JSON.parse(viren?.latestPrep?.questionsJson || "[]"); } catch { return []; } })();
  const sevColor: any = {
    routine: "text-white/55 bg-white/5 border-white/10",
    monitoring: "text-blue-300 bg-blue-500/10 border-blue-500/30",
    attention: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    critical: "text-red-300 bg-red-500/10 border-red-500/30",
  };
  const toneColor: any = { warm: "text-emerald-300", neutral: "text-white/70", firm: "text-amber-300" };

  const priorityColor: any = { high: "text-red-400 bg-red-500/15", medium: "text-amber-400 bg-amber-500/15", low: "text-white/50 bg-white/5" };

  return (
    <div className="space-y-5">
      <Link href="/banker/tasks">
        <a className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm"><ArrowLeft className="w-4 h-4" /> Back to tasks</a>
      </Link>

      {!ownable && (
        <div className="bg-amber-500/[0.08] border border-amber-500/30 rounded-xl p-4 flex items-start gap-3 flex-wrap">
          <div className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0 text-amber-400 text-xs font-bold">!</div>
          <div className="flex-1 min-w-[220px]">
            <p className="text-amber-300 text-sm font-medium">This portco isn't in your wheelhouse — read-only.</p>
            <p className="text-white/60 text-xs mt-0.5">
              {ownerBanker
                ? <>Owned by <span className="text-white">{ownerBanker.name}</span>. You have full visibility but can't change status or upload artifacts. If the workload is heavy, request to cover and admin will route it.</>
                : <>The responsible banker will act. If you want to take this on, request to cover and admin will route it.</>}
            </p>
          </div>
          {task.companyId && (
            <button onClick={() => setReqOpen(true)}
              className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 flex-shrink-0">
              <HandHelping className="w-3.5 h-3.5" /> Request to cover this portco
            </button>
          )}
        </div>
      )}

      <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className={`text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded ${priorityColor[task.priority]}`}>{task.priority}</span>
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-mono">{task.category}</span>
          <span className="text-[10px] uppercase tracking-wider text-[#FF5C00] bg-[#FF5C00]/10 px-1.5 py-0.5 rounded">{task.status.replace("_", " ")}</span>
          <WheelhouseBadge status={wheelhouseStatus} />
        </div>
        <h1 className="text-2xl font-bold">{task.title}</h1>
        <div className="mt-3 flex items-center gap-3 text-sm text-white/60">
          {investor && <span>Investor: <span className="text-white">{investor.name}</span>{investor.firm ? ` · ${investor.firm}` : ""}</span>}
          {company && <span>Company: <span className="text-white">{company.name}</span></span>}
          <span className="text-white/30">· Opened {new Date(task.createdAt).toLocaleString()}</span>
        </div>
      </div>

      {task.investorIntent && (
        <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-6">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2 font-mono">Investor intent (captured by Kavya)</p>
          <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{task.investorIntent}</p>
        </div>
      )}

      <div className="bg-gradient-to-br from-purple-500/[0.06] to-transparent border border-purple-500/25 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-purple-300" />
            </div>
            <div>
              <p className="text-purple-300 text-xs font-semibold uppercase tracking-wider">Viren · Senior Banking Lead</p>
              <p className="text-white/50 text-[11px]">Prep questions before your founder call. He remembers what you've already asked.</p>
            </div>
          </div>
          {ownable && (
            <button onClick={runVirenPrep} disabled={prepping}
              className="bg-purple-500/20 hover:bg-purple-500/30 disabled:opacity-50 text-purple-200 border border-purple-500/40 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
              {prepping ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking</> : <><RefreshCcw className="w-3.5 h-3.5" /> {prepQuestions.length > 0 ? "Refresh questions" : "Prep questions"}</>}
            </button>
          )}
        </div>
        {prepError && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-400 text-xs mb-3">{prepError}</div>}
        {viren?.latestPrep?.callContext && (
          <div className="mb-4 bg-purple-500/[0.07] border border-purple-500/30 rounded-lg p-3.5">
            <p className="text-purple-300 text-[10px] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Context of the Call · what Kavya & Meera know
            </p>
            <p className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap">{viren.latestPrep.callContext}</p>
          </div>
        )}
        {viren?.latestPrep?.rationale && (
          <p className="text-white/75 text-sm leading-relaxed italic mb-3 border-l-2 border-purple-500/40 pl-3">"{viren.latestPrep.rationale}"</p>
        )}
        {prepQuestions.length === 0 ? (
          <p className="text-white/45 text-sm">No question prep yet. {ownable ? "Click \"Prep questions\" — Viren will read Kavya's chat with this investor, Meera's intel on the portco, and prior briefs, then write you a context brief plus 4–7 sharp questions for the founder." : "Owner can ask Viren to prep questions."}</p>
        ) : (
          <ol className="space-y-3">
            {prepQuestions.map((q: any, i: number) => (
              <li key={i} className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <span className="text-purple-300 font-mono text-xs font-bold mt-0.5">{i + 1}.</span>
                  <div className="flex-1">
                    <p className="text-white text-sm leading-snug">{q.question}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[11px]">
                      {q.tone && <span className={`uppercase tracking-wider font-mono ${toneColor[q.tone] || "text-white/50"}`}>{q.tone}</span>}
                      {q.why && <span className="text-white/45 italic">↳ {q.why}</span>}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
        {viren?.latestPrep?.createdAt && (
          <p className="text-white/35 text-[10px] font-mono mt-3">Prepped {new Date(viren.latestPrep.createdAt).toLocaleString()}</p>
        )}
      </div>

      {task.investorId && (
        <div className="bg-gradient-to-br from-[#FF5C00]/[0.06] to-transparent border border-[#FF5C00]/25 rounded-2xl p-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#FF5C00]/20 border border-[#FF5C00]/30 flex items-center justify-center">
                <Send className="w-4 h-4 text-[#FF5C00]" />
              </div>
              <div>
                <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-wider">Outreach to {investor?.name || "investor"}</p>
                <p className="text-white/50 text-[11px]">Reach back out — Kavya drafts, you edit, send (admin reviews if you're not on direct send).</p>
              </div>
            </div>
            {ownable && (
              <button onClick={() => openComposer()}
                className="bg-[#FF5C00]/20 hover:bg-[#FF5C00]/30 text-[#FF5C00] border border-[#FF5C00]/40 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                <Send className="w-3.5 h-3.5" /> Reach out
              </button>
            )}
          </div>
          {outreach.length === 0 ? (
            <p className="text-white/45 text-sm">No outreach yet on this task.</p>
          ) : (
            <div className="space-y-2">
              {outreach.map((o: any) => {
                const lastMsg = o.messages?.[o.messages.length - 1];
                const pillColor = ({
                  draft: "bg-white/[0.05] text-white/60 border-white/15",
                  pending_admin_approval: "bg-amber-500/15 text-amber-300 border-amber-500/30",
                  rejected: "bg-red-500/15 text-red-300 border-red-500/30",
                  approved: "bg-blue-500/15 text-blue-300 border-blue-500/30",
                  sent: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
                  replied: "bg-purple-500/15 text-purple-300 border-purple-500/30",
                  no_reply: "bg-white/[0.05] text-white/45 border-white/15",
                } as Record<string, string>)[o.status] || "bg-white/[0.05] text-white/60 border-white/15";
                const pillLabel = ({
                  draft: "Draft", pending_admin_approval: "Awaiting admin", rejected: "Sent back", approved: "Approved",
                  sent: "Sent", replied: "Investor replied", no_reply: "No reply",
                } as Record<string, string>)[o.status] || o.status;
                return (
                  <button key={o.id} onClick={() => openComposer(o)}
                    className="w-full text-left bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-mono border ${pillColor}`}>{pillLabel}</span>
                      <span className="text-white/85 text-sm font-medium truncate flex-1">{o.subject}</span>
                      <span className="text-white/35 text-[10px] font-mono">{new Date(o.updatedAt).toLocaleDateString()}</span>
                    </div>
                    {lastMsg && <p className="text-white/55 text-xs line-clamp-2">{lastMsg.body}</p>}
                    {o.status === "rejected" && o.adminNote && (
                      <p className="text-red-300/85 text-[11px] mt-1 italic">Admin note: {o.adminNote}</p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {task.meeraBrief && (
        <div className="bg-gradient-to-br from-emerald-500/[0.05] to-transparent border border-emerald-500/25 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
              <span className="text-emerald-400 font-bold text-xs">M</span>
            </div>
            <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">Meera's brief</p>
          </div>
          <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{task.meeraBrief}</p>
        </div>
      )}

      <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <p className="text-[10px] uppercase tracking-widest text-white/40 font-mono">Status</p>
          {ownable && task.status !== "closed" && task.status !== "dismissed" && (
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => setDismissOpen(true)}
                className="bg-white/[0.04] hover:bg-white/[0.08] text-white/65 hover:text-white border border-white/15 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
                title="Dismiss without closing — for false positives, duplicates, or tasks Kavya already answered.">
                <Trash2 className="w-3.5 h-3.5" /> Dismiss
              </button>
              {task.investorId && (
                <button
                  onClick={() => {
                    // Pre-fill from existing schedule so re-opening the modal
                    // edits the current call instead of forcing the banker to
                    // retype the link from scratch.
                    if (task.meetingLink) setSchedLink(task.meetingLink);
                    if (task.scheduledFor) {
                      // datetime-local needs YYYY-MM-DDTHH:mm in local TZ.
                      const d = new Date(task.scheduledFor);
                      const pad = (n: number) => String(n).padStart(2, "0");
                      setSchedTime(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                    }
                    if (task.meetingNote) setSchedNote(task.meetingNote);
                    setScheduleOpen(true);
                  }}
                  className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border border-blue-500/30 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
                  title="Schedule a call with the investor. This is not a closure — the loop closes when you resolve after the call.">
                  <CalendarClock className="w-3.5 h-3.5" />
                  {task.scheduledFor ? "Update call" : "Schedule call"}
                </button>
              )}
              <button onClick={() => setResolveOpen(true)}
                className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {task.investorId ? "Resolve & notify investor" : "Mark resolved"}
              </button>
            </div>
          )}
        </div>
        {task.status === "closed" || task.status === "dismissed" ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${task.status === "dismissed" ? "bg-white/[0.04] border-white/15 text-white/55" : "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"}`}>
              {task.status === "dismissed" ? "dismissed" : (task.reopenCount ? `closed (after ${task.reopenCount} reopen${task.reopenCount > 1 ? "s" : ""})` : "closed")}
            </span>
          </div>
        ) : (
          // "closed" intentionally excluded from this row — closing requires a
          // structured resolution note and is gated behind the Resolve modal.
          // Letting bankers click a pill bypassed the loop and silently 400'd.
          <div className="flex items-center gap-2 flex-wrap">
            {["pending", "in_progress", "awaiting_founder", "artifact_uploaded", "reopened"].map(s => (
              <button key={s} disabled={savingStatus || !ownable} onClick={() => setStatus(s)}
                title={!ownable ? "Outside your coverage — read only" : ""}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${task.status === s ? "bg-[#FF5C00] border-[#FF5C00] text-white" : "bg-white/[0.04] border-white/[0.1] text-white/60 hover:border-white/[0.2]"} ${!ownable ? "opacity-40 cursor-not-allowed hover:border-white/[0.1]" : ""}`}>
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        )}
        {task.status === "closed" && task.closedAt && (
          <p className="text-white/40 text-[11px] font-mono mt-3">Closed {new Date(task.closedAt).toLocaleString()}</p>
        )}
        {task.status === "dismissed" && task.dismissedAt && (
          <p className="text-white/40 text-[11px] font-mono mt-3">Dismissed {new Date(task.dismissedAt).toLocaleString()}{task.dismissReason ? ` — ${String(task.dismissReason).split("::")[0].replace(/_/g, " ")}` : ""}</p>
        )}
      </div>

      {resolveOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={tryDismissResolve}>
          <div className="force-dark-surface bg-[#0E0E0E] border border-white/15 rounded-2xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-bold text-lg">Resolve this task</h3>
                <p className="text-white/55 text-sm mt-1">
                  {task.investorId && !resolveSilent
                    ? "Write a one-line note for the investor — they'll see it in their inbox. The loop only closes when they hear back."
                    : task.investorId
                      ? "Closing silently. The investor won't be notified — use this only for duplicates or things Kavya already answered."
                      : "This task has no investor attached, so closing is silent. Add a note for your own audit trail if useful."}
                </p>
              </div>
              <button onClick={tryDismissResolve} className="text-white/40 hover:text-white p-1"><X className="w-4 h-4" /></button>
            </div>
            {resolveError && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-400 text-xs mb-3">{resolveError}</div>}
            {/* Structured resolution (S5). Three short fields beat one long
                paragraph because each has its own per-field bar — investors
                actually read 3×40 chars, they skim a 280-char wall. */}
            {task.investorId && !resolveSilent ? (
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/55 font-semibold">What changed <span className="text-white/30 font-normal">(15–120)</span></label>
                  <input
                    autoFocus
                    type="text"
                    value={resolveWhatChanged}
                    onChange={e => setResolveWhatChanged(e.target.value.replace(/\r?\n/g, " "))}
                    placeholder="e.g. Founder confirmed Q3 revenue at ₹4.2 Cr"
                    maxLength={140}
                    className="w-full mt-1 bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/35"
                  />
                  <span className={`text-[10px] font-mono ${resolveWhatChanged.length > 120 ? "text-red-400" : "text-white/35"}`}>{resolveWhatChanged.length}/120</span>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/55 font-semibold">What you should know <span className="text-white/30 font-normal">(15–200)</span></label>
                  <input
                    type="text"
                    value={resolveWhatToKnow}
                    onChange={e => setResolveWhatToKnow(e.target.value.replace(/\r?\n/g, " "))}
                    placeholder="e.g. MoM growth holding at 18%, but burn ticked up to ₹85L"
                    maxLength={220}
                    className="w-full mt-1 bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/35"
                  />
                  <span className={`text-[10px] font-mono ${resolveWhatToKnow.length > 200 ? "text-red-400" : "text-white/35"}`}>{resolveWhatToKnow.length}/200</span>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/55 font-semibold">Next step <span className="text-white/30 font-normal">(optional, ≤100)</span></label>
                  <input
                    type="text"
                    value={resolveNextStep}
                    onChange={e => setResolveNextStep(e.target.value.replace(/\r?\n/g, " "))}
                    placeholder="e.g. Diligence pack attached — review and revert"
                    maxLength={120}
                    className="w-full mt-1 bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/35"
                  />
                  <span className={`text-[10px] font-mono ${resolveNextStep.length > 100 ? "text-red-400" : "text-white/35"}`}>{resolveNextStep.length}/100</span>
                </div>
              </div>
            ) : (
              <textarea
                autoFocus
                value={resolveWhatChanged}
                onChange={e => setResolveWhatChanged(e.target.value.replace(/\r?\n/g, " "))}
                onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}
                placeholder="Optional internal note"
                rows={3}
                maxLength={RESOLUTION_MAX + 20}
                className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/35 resize-none"
              />
            )}
            {task.investorId && (
              <label className="flex items-center gap-2 mt-3 cursor-pointer text-white/65 text-xs">
                <input
                  type="checkbox"
                  checked={resolveSilent}
                  onChange={e => setResolveSilent(e.target.checked)}
                  className="accent-amber-500"
                />
                Close silently — don't notify the investor
              </label>
            )}
            {(() => {
              const requireIntent = !!task.investorId && !resolveSilent;
              // Validation differs by mode:
              //  - Structured (notify path): each field has its own bar; the
              //    submit button locks until all required fields meet length.
              //  - Free-form (silent / no-investor path): the legacy
              //    one-sentence rules apply to whatever was typed.
              let validationMsg: string | null = null;
              if (requireIntent) {
                const wcLen = resolveWhatChanged.trim().length;
                const wkLen = resolveWhatToKnow.trim().length;
                if (wcLen < 15) validationMsg = `What changed: add ${15 - wcLen} more character${15 - wcLen === 1 ? "" : "s"}.`;
                else if (wcLen > 120) validationMsg = `What changed: trim ${wcLen - 120}.`;
                else if (wkLen < 15) validationMsg = `What you should know: add ${15 - wkLen} more character${15 - wkLen === 1 ? "" : "s"}.`;
                else if (wkLen > 200) validationMsg = `What you should know: trim ${wkLen - 200}.`;
                else if (resolveNextStep.trim().length > 100) validationMsg = `Next step: trim ${resolveNextStep.trim().length - 100}.`;
              } else {
                // Silent / no-investor: free-text in resolveWhatChanged.
                const t = resolveWhatChanged.trim();
                if (t.length > 0) validationMsg = validateResolutionLocal(t);
              }
              const blocked = resolving || (requireIntent && validationMsg !== null);
              return (
                <>
                  <div className="flex items-center justify-end mt-4">
                    <div className="flex gap-2">
                      <button onClick={tryDismissResolve} disabled={resolving}
                        className="px-3 py-1.5 rounded-lg text-xs text-white/70 hover:bg-white/[0.06] disabled:opacity-50">Cancel</button>
                      <button onClick={submitResolve} disabled={blocked}
                        className="bg-emerald-500/20 hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-200 border border-emerald-500/40 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                        {resolving
                          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Closing</>
                          : <><Send className="w-3.5 h-3.5" /> {requireIntent ? "Close & notify" : "Close task"}</>}
                      </button>
                    </div>
                  </div>
                  {validationMsg && (
                    <p className="text-amber-400/85 text-[11px] mt-2">{validationMsg}</p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {dismissOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !dismissing && setDismissOpen(false)}>
          <div className="force-dark-surface bg-[#0E0E0E] border border-white/15 rounded-2xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-bold text-lg">Dismiss this task</h3>
                <p className="text-white/55 text-sm mt-1">
                  Use this when the task shouldn't have existed — false positive, duplicate, or Kavya already handled it. The investor isn't notified, but the reason is logged so we can tune Meera over time.
                </p>
              </div>
              <button onClick={() => !dismissing && setDismissOpen(false)} className="text-white/40 hover:text-white p-1"><X className="w-4 h-4" /></button>
            </div>
            {dismissError && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-400 text-xs mb-3">{dismissError}</div>}
            <div className="space-y-2">
              {DISMISS_REASONS.map(r => (
                <label key={r.value} className={`block p-3 rounded-lg border cursor-pointer transition-colors ${dismissReason === r.value ? "border-[#FF5C00]/50 bg-[#FF5C00]/[0.06]" : "border-white/10 hover:border-white/20 hover:bg-white/[0.03]"}`}>
                  <div className="flex items-start gap-2">
                    <input type="radio" name="dismiss-reason" value={r.value} checked={dismissReason === r.value}
                      onChange={() => setDismissReason(r.value)} className="mt-0.5 accent-[#FF5C00]" />
                    <div>
                      <div className="text-white text-sm font-medium">{r.label}</div>
                      <div className="text-white/50 text-xs mt-0.5">{r.help}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <textarea
              value={dismissNote}
              onChange={e => setDismissNote(e.target.value.slice(0, DISMISS_NOTE_MAX))}
              placeholder="Required — explain in one line why. The investor sees this. e.g. 'Duplicate of task #312, already resolved last week.'"
              rows={2}
              maxLength={DISMISS_NOTE_MAX}
              className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/35 resize-none mt-3"
            />
            <p className="text-white/45 text-[11px] mt-1.5">
              At least {DISMISS_NOTE_MIN} characters. The investor reads this in their dashboard — give them something honest.
            </p>
            {(() => {
              const noteTrim = dismissNote.trim();
              const noteShort = noteTrim.length < DISMISS_NOTE_MIN;
              const blocked = dismissing || !dismissReason || noteShort;
              return (
                <div className="flex items-center justify-between mt-3">
                  <span className={`text-[11px] font-mono ${noteShort ? "text-amber-400/85" : "text-white/35"}`}>
                    {noteTrim.length}/{DISMISS_NOTE_MAX}{noteShort && noteTrim.length > 0 && ` (${DISMISS_NOTE_MIN - noteTrim.length} more)`}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={() => setDismissOpen(false)} disabled={dismissing}
                      className="px-3 py-1.5 rounded-lg text-xs text-white/70 hover:bg-white/[0.06] disabled:opacity-50">Cancel</button>
                    <button onClick={submitDismiss} disabled={blocked}
                      className="bg-white/10 hover:bg-white/15 disabled:opacity-50 disabled:cursor-not-allowed text-white border border-white/20 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                      {dismissing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Dismissing</> : <><Trash2 className="w-3.5 h-3.5" /> Dismiss task</>}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {scheduleOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !scheduling && setScheduleOpen(false)}>
          <div className="force-dark-surface bg-[#0E0E0E] border border-white/15 rounded-2xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-bold text-lg flex items-center gap-2"><CalendarClock className="w-5 h-5 text-blue-300" /> Schedule a call</h3>
                <p className="text-white/55 text-sm mt-1">
                  The investor will see this on their dashboard with a calendar download. Scheduling does not close the task — resolve it after the call so the loop is honest.
                </p>
              </div>
              <button onClick={() => !scheduling && setScheduleOpen(false)} className="text-white/40 hover:text-white p-1"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3 mt-4">
              {/* Mode switch: confirmed vs propose */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-white/[0.04] border border-white/10 rounded-lg">
                <button
                  type="button"
                  onClick={() => setSchedMode("confirmed")}
                  className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${schedMode === "confirmed" ? "bg-blue-500/30 text-blue-100 border border-blue-400/40" : "text-white/55 hover:bg-white/[0.04]"}`}
                >Confirm a time</button>
                <button
                  type="button"
                  onClick={() => setSchedMode("propose")}
                  className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${schedMode === "propose" ? "bg-orange-500/30 text-orange-100 border border-orange-400/40" : "text-white/55 hover:bg-white/[0.04]"}`}
                >Propose 1-3 slots</button>
              </div>
              <p className="text-white/45 text-xs leading-relaxed">
                {schedMode === "confirmed"
                  ? "You set a single time and an invite goes out immediately."
                  : "Investor receives a Kavya email with up to 3 slots and clicks one to confirm. The first slot is held tentatively on your calendar."}
              </p>

              {schedMode === "confirmed" ? (
                <>
                  <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                    <input
                      type="checkbox"
                      checked={autoCreateMeet}
                      onChange={e => setAutoCreateMeet(e.target.checked)}
                      className="mt-0.5 accent-blue-500 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm flex items-center gap-1.5"><Video className="w-3.5 h-3.5 text-blue-300" /> Create a Google Meet automatically</p>
                      <p className="text-white/50 text-xs mt-0.5">
                        Sends a calendar invite to both of you, records the call, and surfaces the recording here once it's done.
                      </p>
                    </div>
                  </label>
                  {!autoCreateMeet && (
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-white/40 font-mono">Meeting link</label>
                      <input
                        type="text"
                        value={schedLink}
                        onChange={e => setSchedLink(e.target.value.slice(0, 500))}
                        placeholder="https://meet.google.com/... or tel:+91..."
                        className="mt-1 w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-blue-400/40"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-white/40 font-mono">When (your local time)</label>
                    <input
                      type="datetime-local"
                      value={schedTime}
                      onChange={e => setSchedTime(e.target.value)}
                      className="mt-1 w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-400/40"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-white/40 font-mono">Slot duration</label>
                    <select
                      value={proposeDuration}
                      onChange={e => setProposeDuration(parseInt(e.target.value, 10))}
                      className="mt-1 w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-400/40"
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>60 minutes</option>
                      <option value={90}>90 minutes</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-white/40 font-mono">Proposed slots ({proposeSlots.length}/3, your local time)</label>
                    <div className="space-y-2 mt-1">
                      {proposeSlots.map((s, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-white/40 text-xs font-mono w-4">{i + 1}.</span>
                          <input
                            type="datetime-local"
                            value={s}
                            onChange={e => {
                              const next = [...proposeSlots]; next[i] = e.target.value; setProposeSlots(next);
                            }}
                            className="flex-1 bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-400/40"
                          />
                          {proposeSlots.length > 1 && (
                            <button type="button" onClick={() => setProposeSlots(proposeSlots.filter((_, idx) => idx !== i))}
                              className="text-white/40 hover:text-red-300 p-1 text-xs">×</button>
                          )}
                        </div>
                      ))}
                    </div>
                    {proposeSlots.length < 3 && (
                      <button type="button" onClick={() => setProposeSlots([...proposeSlots, ""])}
                        className="mt-2 text-orange-300 hover:text-orange-200 text-xs font-medium">+ Add another slot</button>
                    )}
                  </div>
                </>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/40 font-mono">{schedMode === "propose" ? "Agenda for the investor" : "Note for the investor (optional)"}</label>
                <textarea
                  value={schedNote}
                  onChange={e => setSchedNote(e.target.value.slice(0, 1000))}
                  rows={3}
                  placeholder="e.g. We'll cover Q3 metrics and the upcoming round."
                  className="mt-1 w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-blue-400/40 resize-none"
                />
                <p className="text-white/30 text-[10px] font-mono mt-1">{schedNote.length}/1000</p>
              </div>
              {schedError && <p className="text-red-400 text-xs">{schedError}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setScheduleOpen(false)} disabled={scheduling}
                  className="px-3 py-1.5 rounded-lg text-xs text-white/70 hover:bg-white/[0.06] disabled:opacity-50">Cancel</button>
                {schedMode === "confirmed" ? (
                  <button onClick={submitSchedule} disabled={scheduling || !schedTime || (!autoCreateMeet && !schedLink.trim())}
                    className="bg-blue-500/20 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-blue-200 border border-blue-400/40 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                    {scheduling ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {autoCreateMeet ? "Creating Meet" : "Sending"}</> : <><Send className="w-3.5 h-3.5" /> {autoCreateMeet ? "Create Meet & invite" : "Send to investor"}</>}
                  </button>
                ) : (
                  <button onClick={submitSchedule} disabled={scheduling || proposeSlots.filter(s => s.trim()).length < 1}
                    className="bg-orange-500/20 hover:bg-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-orange-200 border border-orange-400/40 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5">
                    {scheduling ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending</> : <><Send className="w-3.5 h-3.5" /> Send proposal</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {task.scheduledFor && task.status !== "closed" && task.status !== "dismissed" && (
        <div className="bg-blue-500/[0.06] border border-blue-500/25 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <Phone className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-blue-200 text-xs uppercase tracking-wider font-medium">Call scheduled</p>
              <p className="text-white text-sm mt-1">
                {new Date(task.scheduledFor).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </p>
              {task.meetingLink && (
                <a href={task.meetingLink} target="_blank" rel="noopener noreferrer"
                  className="text-blue-300 hover:text-blue-200 text-xs mt-1 inline-flex items-center gap-1 break-all">
                  <ExternalLink className="w-3 h-3 shrink-0" /> {task.meetingLink}
                </a>
              )}
              {task.meetingNote && (
                <p className="text-white/70 text-xs mt-2 italic border-l-2 border-blue-500/30 pl-2">
                  {task.meetingNote}
                </p>
              )}
              <p className="text-white/40 text-[11px] mt-2">
                Resolve the task after the call so the loop closes honestly.
              </p>
            </div>
          </div>
        </div>
      )}

      {callRecording && (() => {
        const start = new Date(callRecording.scheduledStartUtc);
        const callIsPast = start.getTime() < Date.now();
        const haveRecording = !!callRecording.recordingUrl;
        // Show this block when (a) we already have a recording, OR (b) the
        // call has happened and we're waiting on the cron. Hide it pre-call —
        // the "Call scheduled" panel above already covers that state.
        if (!haveRecording && !callIsPast) return null;
        return (
          <div className="bg-purple-500/[0.06] border border-purple-500/25 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <Video className="w-4 h-4 text-purple-300 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-purple-200 text-xs uppercase tracking-wider font-medium">Recording</p>
                {haveRecording ? (
                  <>
                    <a href={callRecording.recordingUrl!} target="_blank" rel="noopener noreferrer"
                      className="text-white text-sm mt-1 inline-flex items-center gap-1.5 hover:text-purple-200 break-all">
                      <ExternalLink className="w-3.5 h-3.5 shrink-0" /> Watch on Google Drive
                    </a>
                    {callRecording.recordingDurationSec != null && (
                      <p className="text-white/50 text-xs mt-1 font-mono">
                        {Math.round(callRecording.recordingDurationSec / 60)} min
                        {callRecording.recordingFetchedAt && (
                          <span className="text-white/30"> · indexed {new Date(callRecording.recordingFetchedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                        )}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-white/70 text-sm mt-1">
                      Recording will appear here within an hour of the call ending.
                    </p>
                    <p className="text-white/40 text-[11px] mt-1 font-mono">
                      Status: {callRecording.status}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">Artifacts ({artifacts.length})</h2>
          {ownable ? (
            <button onClick={() => setUploadOpen(!uploadOpen)} className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
              <Upload className="w-4 h-4" /> Upload artifact
            </button>
          ) : (
            <span className="text-white/40 text-xs font-mono">Upload disabled — read-only</span>
          )}
        </div>

        {uploadOpen && (
          <div className="bg-[#0A0A0A] border border-white/[0.1] rounded-xl p-5 mb-4 space-y-3" data-testid="artifact-upload-modal">
            {uploadError && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-400 text-xs">{uploadError}</div>}

            {/* Tabs — file upload is the default. Link/notes is the fallback
                for Drive-link deliveries that don't need bytes to move. */}
            <div className="flex gap-1 border-b border-white/10 mb-2">
              <button onClick={() => setUploadTab("file")} data-testid="tab-upload-file"
                className={`px-3 py-2 text-xs font-medium border-b-2 transition flex items-center gap-1.5 ${uploadTab === "file" ? "border-[#FF5C00] text-[#FF5C00]" : "border-transparent text-white/50 hover:text-white"}`}>
                <FileUp className="w-3.5 h-3.5" /> Upload file
              </button>
              <button onClick={() => setUploadTab("link")} data-testid="tab-paste-link"
                className={`px-3 py-2 text-xs font-medium border-b-2 transition flex items-center gap-1.5 ${uploadTab === "link" ? "border-[#FF5C00] text-[#FF5C00]" : "border-transparent text-white/50 hover:text-white"}`}>
                <LinkIcon className="w-3.5 h-3.5" /> Paste link / notes
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-white/60 text-xs mb-1.5">Kind</label>
                <select value={uploadForm.kind} onChange={(e) => setUploadForm(f => ({ ...f, kind: e.target.value }))}
                  className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm">
                  <option value="zoom_transcript">Zoom transcript</option>
                  <option value="email_thread">Email thread</option>
                  <option value="pdf">PDF / document</option>
                  <option value="drive_link">Google Drive link</option>
                  <option value="note">Meeting note</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-white/60 text-xs mb-1.5">Title</label>
                <input value={uploadForm.title} onChange={(e) => setUploadForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Portfolio analysis for Prayush — May 2026"
                  data-testid="artifact-title"
                  className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>

            {uploadTab === "file" ? (
              <div>
                <label className="block text-white/60 text-xs mb-1.5 flex items-center gap-1"><FileUp className="w-3 h-3" /> File (PDF, CSV, XLSX, DOCX, PNG/JPG, TXT — max 25 MB)</label>
                <div className="border border-dashed border-white/20 rounded-lg p-4 bg-white/[0.02]">
                  <input type="file" data-testid="artifact-file-input"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) setUploadFile(f); }}
                    accept=".pdf,.csv,.xlsx,.xls,.docx,.doc,.png,.jpg,.jpeg,.webp,.txt"
                    className="block w-full text-xs text-white/70 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#FF5C00] file:text-white hover:file:bg-[#E55200]" />
                  {uploadFile && (
                    <p className="text-xs text-white/70 mt-2 font-mono">
                      {uploadFile.name} · {(uploadFile.size / 1024 / 1024).toFixed(2)} MB · {uploadFile.type || "unknown"}
                    </p>
                  )}
                </div>
                <p className="text-[10px] text-white/40 mt-1">The investor will receive an email with a download link and the file will appear in their "Files from advisor" tab.</p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-white/60 text-xs mb-1.5 flex items-center gap-1"><LinkIcon className="w-3 h-3" /> URL (public link only — no local paths)</label>
                  <input value={uploadForm.url} onChange={(e) => setUploadForm(f => ({ ...f, url: e.target.value }))}
                    placeholder="https://drive.google.com/..." className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-white/60 text-xs mb-1.5 flex items-center gap-1"><FileText className="w-3 h-3" /> Or paste content (transcript / email text)</label>
                  <textarea value={uploadForm.content} onChange={(e) => setUploadForm(f => ({ ...f, content: e.target.value }))}
                    rows={6} placeholder="Paste Zoom transcript, email thread, or meeting notes here. Meera will read it and summarize."
                    className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm font-mono leading-relaxed" />
                </div>
              </>
            )}

            {uploading && (
              <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3">
                <ProgressLoader
                  active={uploading}
                  title={uploadTab === "file" ? `Uploading${uploadProgress > 0 && uploadProgress < 99 ? ` — ${uploadProgress}%` : ""}` : "Saving artifact"}
                  stages={uploadTab === "file" ? [
                    "Requesting a signed upload slot",
                    "Sending the file to storage",
                    "Recording the artifact + cohort",
                    "Notifying the investor by email",
                    "Confirming with your inbox",
                  ] : [
                    "Recording the artifact",
                    "Asking Meera to summarise",
                    "Looping the investor in",
                  ]}
                  stageIntervalMs={2500}
                />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button onClick={handleUpload} disabled={uploading}
                data-testid="submit-artifact-upload"
                className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Working...</> : <><CheckCircle2 className="w-4 h-4" /> Save & notify investor</>}
              </button>
              <button onClick={() => { setUploadOpen(false); setUploadFile(null); }} disabled={uploading}
                className="text-white/60 hover:text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">Cancel</button>
            </div>
          </div>
        )}

        {artifacts.length === 0 ? (
          <p className="text-white/40 text-sm text-center py-6">No artifacts yet. Once you upload the first one, Meera will auto-synthesise and loop the investor in.</p>
        ) : (
          <div className="space-y-2">
            {artifacts.map((a: any) => (
              <div key={a.id} className="bg-white/[0.03] border border-white/[0.08] rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-white/40 font-mono mb-1">{a.kind.replace("_", " ")}</p>
                    <p className="text-white font-medium text-sm">{a.title}</p>
                    {a.url && <a href={a.url} target="_blank" rel="noreferrer" className="text-[#FF5C00] hover:underline text-xs flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" /> {a.url.length > 60 ? a.url.slice(0, 57) + "..." : a.url}</a>}
                    {a.fileUrl && (
                      <a href={`${import.meta.env.BASE_URL}api/banker/artifacts/${a.id}/download?token=${encodeURIComponent(localStorage.getItem("banker_token") || "")}`}
                        data-testid={`download-artifact-${a.id}`}
                        className="text-[#FF5C00] hover:underline text-xs flex items-center gap-1 mt-1">
                        <ExternalLink className="w-3 h-3" /> Download {a.fileName || "file"}{a.fileSize ? ` (${Math.round(a.fileSize / 1024)} KB)` : ""}
                      </a>
                    )}
                  </div>
                  <span className="text-white/30 text-[10px]">{new Date(a.createdAt).toLocaleDateString()}</span>
                </div>
                {a.summary && (
                  <div className="mt-3 bg-emerald-500/[0.05] border border-emerald-500/20 rounded p-3">
                    <p className="text-emerald-400 text-[10px] uppercase tracking-wider font-semibold mb-1">Meera's summary</p>
                    <p className="text-white/80 text-xs leading-relaxed whitespace-pre-wrap">{a.summary}</p>
                  </div>
                )}
                {(() => {
                  const sugs = suggestionsByArtifact[a.id] || [];
                  const pending = sugs.filter((s: any) => s.status === "pending");
                  const decided = sugs.filter((s: any) => s.status !== "pending");
                  const hasContent = (a.content && a.content.length > 80) || (a.summary && a.summary.length > 40);
                  if (sugs.length === 0 && !hasContent) return null;
                  return (
                    <div className="mt-3 bg-[#FF5C00]/[0.04] border border-[#FF5C00]/25 rounded p-3 space-y-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-[#FF5C00] text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3" /> Suggested next-actions ({pending.length} open{decided.length > 0 ? `, ${decided.length} decided` : ""})
                        </p>
                        {ownable && sugs.length === 0 && hasContent && (
                          <button onClick={() => processArtifact(a.id)} disabled={processingArtifact[a.id]}
                            className="text-[10px] uppercase tracking-wider font-mono text-white/60 hover:text-white border border-white/15 rounded px-2 py-0.5 disabled:opacity-50">
                            {processingArtifact[a.id] ? "Working..." : "Extract now"}
                          </button>
                        )}
                      </div>
                      {sugs.length === 0 && (
                        <p className="text-white/45 text-xs italic">{ownable ? "Extractor hasn't run yet — try the button above, or wait a few seconds after upload." : "No extracted suggestions yet — they'll appear once the covering banker runs the extractor."}</p>
                      )}
                      {pending.map((s: any) => {
                        const ed = suggestionEditor[s.id];
                        const editing = !!ed;
                        const roleColor = s.suggestedRole === "investor" ? "bg-blue-500/15 text-blue-300 border-blue-500/30"
                          : s.suggestedRole === "founder" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                          : "bg-[#FF5C00]/15 text-[#FF5C00] border-[#FF5C00]/30";
                        const priColor = s.suggestedPriority === "high" ? "bg-red-500/15 text-red-300 border-red-500/30"
                          : s.suggestedPriority === "low" ? "bg-white/5 text-white/45 border-white/10"
                          : "bg-amber-500/10 text-amber-300 border-amber-500/25";
                        return (
                          <div key={s.id} className="bg-black/30 border border-white/10 rounded p-2.5 space-y-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border bg-white/5 text-white/60 border-white/10">{s.kind}</span>
                              <span className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${roleColor}`}>{s.suggestedRole}</span>
                              <span className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${priColor}`}>{s.suggestedPriority}</span>
                              <span className="text-[9px] uppercase tracking-wider font-mono text-white/40">due in {s.suggestedDueDays}d</span>
                            </div>
                            {editing ? (
                              <div className="space-y-2">
                                <input value={ed.title} onChange={(e) => setSuggestionEditor(prev => ({ ...prev, [s.id]: { ...prev[s.id], title: e.target.value } }))}
                                  className="w-full bg-white/[0.06] border border-white/15 rounded px-2 py-1.5 text-xs" />
                                <div className="flex items-center gap-2 text-[10px]">
                                  <label className="text-white/45">Priority</label>
                                  <select value={ed.priority} onChange={(e) => setSuggestionEditor(prev => ({ ...prev, [s.id]: { ...prev[s.id], priority: e.target.value } }))}
                                    className="bg-white/[0.06] border border-white/15 rounded px-1.5 py-1 text-[10px]">
                                    <option value="high">high</option><option value="medium">medium</option><option value="low">low</option>
                                  </select>
                                  <label className="text-white/45 ml-2">Due (days)</label>
                                  <input type="number" min={1} max={30} value={ed.dueDays}
                                    onChange={(e) => setSuggestionEditor(prev => ({ ...prev, [s.id]: { ...prev[s.id], dueDays: Math.max(1, Math.min(30, Number(e.target.value) || 1)) } }))}
                                    className="w-14 bg-white/[0.06] border border-white/15 rounded px-1.5 py-1 text-[10px]" />
                                </div>
                              </div>
                            ) : (
                              <>
                                <p className="text-white text-sm font-medium leading-snug">{s.title}</p>
                                {s.body && <p className="text-white/65 text-xs leading-relaxed">{s.body}</p>}
                              </>
                            )}
                            {ownable && (
                              <div className="flex items-center gap-2 pt-1">
                                {!editing && (
                                  <button onClick={() => setSuggestionEditor(prev => ({ ...prev, [s.id]: { title: s.title, priority: s.suggestedPriority, dueDays: s.suggestedDueDays } }))}
                                    className="text-[10px] text-white/55 hover:text-white underline underline-offset-2">Edit before accepting</button>
                                )}
                                <button onClick={() => acceptSuggestion(s)} disabled={suggestionPending[s.id]}
                                  className="ml-auto bg-[#FF5C00] hover:bg-[#E55200] text-white text-[11px] px-3 py-1 rounded font-medium disabled:opacity-50">
                                  {suggestionPending[s.id] ? "..." : editing ? "Accept edited" : "Accept as task"}
                                </button>
                                <button onClick={() => dismissSuggestion(s)} disabled={suggestionPending[s.id]}
                                  className="text-white/50 hover:text-white text-[11px] px-2 py-1 rounded border border-white/15 disabled:opacity-50">Dismiss</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {decided.length > 0 && (
                        <details className="text-[11px] text-white/45">
                          <summary className="cursor-pointer hover:text-white/70">Show {decided.length} decided</summary>
                          <ul className="mt-2 space-y-1 pl-2 border-l border-white/10">
                            {decided.map((s: any) => (
                              <li key={s.id} className="leading-relaxed">
                                <span className={s.status === "accepted" ? "text-emerald-400" : "text-white/35"}>
                                  {s.status === "accepted" ? "✓" : "✕"}
                                </span>{" "}
                                <span className={s.status === "accepted" ? "text-white/70" : "text-white/35 line-through"}>{s.title}</span>
                                {s.createdTaskId && (
                                  <Link href={`/banker/tasks/${s.createdTaskId}`}>
                                    <a className="ml-2 text-[#FF5C00] hover:underline">→ task #{s.createdTaskId}</a>
                                  </Link>
                                )}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  );
                })()}
                {viren?.artifactDigests?.[a.id] && (() => {
                  const d = viren.artifactDigests[a.id];
                  return (
                    <div className="mt-3 bg-purple-500/[0.05] border border-purple-500/25 rounded p-3 space-y-2.5">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-purple-300 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Viren's read</p>
                        <div className="flex items-center gap-1.5">
                          {d.severity && <span className={`text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${sevColor[d.severity]}`}>{d.severity}</span>}
                          {d.bankerOverreaction === true && <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> reframed</span>}
                        </div>
                      </div>
                      {d.founderResponseDraft && (
                        <div>
                          <p className="text-white/45 text-[10px] uppercase tracking-wider font-mono mb-1">Suggested founder-facing next step</p>
                          <p className="text-white/85 text-xs leading-relaxed whitespace-pre-wrap">{d.founderResponseDraft}</p>
                        </div>
                      )}
                      {d.meeraUpdate && (
                        <div>
                          <p className="text-white/45 text-[10px] uppercase tracking-wider font-mono mb-1">Update sent to Meera</p>
                          <p className="text-white/70 text-xs leading-relaxed whitespace-pre-wrap italic border-l border-purple-500/30 pl-2">{d.meeraUpdate}</p>
                        </div>
                      )}
                      {d.rationale && (
                        <details className="text-white/55 text-[11px]">
                          <summary className="cursor-pointer hover:text-white/75">Viren's reasoning</summary>
                          <p className="mt-1.5 pl-2 border-l border-white/10 leading-relaxed">{d.rationale}</p>
                        </details>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gradient-to-br from-[#FF5C00]/10 to-transparent border border-[#FF5C00]/25 rounded-2xl p-5 flex items-center gap-3">
        <MessageCircle className="w-5 h-5 text-[#FF5C00] flex-shrink-0" />
        <p className="text-white/75 text-sm flex-1">Need context? Ask Meera about this task.</p>
        <Link href="/banker/meera"><a className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap">Ask Meera</a></Link>
      </div>

      {reqOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/[0.1] rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-white flex items-center gap-2"><HandHelping className="w-5 h-5 text-amber-400" /> Request to cover this portco</h2>
              <button onClick={() => setReqOpen(false)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-white/60 text-xs leading-relaxed mb-4">
              The admin (cohort lead) will see this request and decide whether to add you as a coverer for <span className="text-white font-medium">{company?.name || "this portco"}</span>.
              You'll get a notification with their decision.
            </p>
            {reqOk ? (
              <div className="bg-emerald-500/10 border border-emerald-500/25 rounded p-3 text-emerald-300 text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Sent. Admin will decide soon.
              </div>
            ) : (
              <>
                {reqError && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-400 text-xs mb-3">{reqError}</div>}
                <label className="block text-white/60 text-xs mb-1.5">Reason (optional)</label>
                <textarea value={reqReason} onChange={(e) => setReqReason(e.target.value)} rows={4}
                  placeholder="e.g. I have prior context with this founder, or the current coverer is overloaded."
                  className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 resize-none" />
                <div className="flex gap-2 mt-4">
                  <button onClick={submitReallocation} disabled={reqSaving}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-black py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                    {reqSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : "Send request"}
                  </button>
                  <button onClick={() => setReqOpen(false)} className="px-4 py-2.5 border border-white/15 rounded-lg text-sm text-white/70">Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {outreachOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !outreachSending && setOutreachOpen(false)}>
          <div className="force-dark-surface bg-[#0E0E0E] border border-white/15 rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#FF5C00] font-mono">Outreach to {investor?.name || "investor"}</p>
                <p className="text-white/85 text-lg font-semibold mt-1">
                  {outreachActive ? ({
                    draft: "Edit and send", pending_admin_approval: "Awaiting admin review", rejected: "Sent back — edit & resubmit",
                    approved: "Approved", sent: "Sent", replied: "Investor replied", no_reply: "No reply",
                  } as Record<string, string>)[outreachActive.status] || outreachActive.status : "New outreach"}
                </p>
              </div>
              <button onClick={() => !outreachSending && setOutreachOpen(false)} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            {outreachLoading ? (
              <div className="py-12 flex items-center justify-center text-white/55 text-sm gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Kavya is drafting...</div>
            ) : (
              <>
                {outreachError && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-300 text-xs mb-3">{outreachError}</div>}
                {outreachActive?.status === "pending_admin_approval" && (
                  <div className="bg-amber-500/10 border border-amber-500/25 rounded p-3 text-amber-300 text-xs mb-3">Sent to admin for review. You'll be notified once approved (or sent back with edits).</div>
                )}
                {outreachActive?.status === "rejected" && outreachActive?.adminNote && (
                  <div className="bg-red-500/10 border border-red-500/25 rounded p-3 text-red-300 text-xs mb-3">Admin sent it back: {outreachActive.adminNote}</div>
                )}
                {(outreachActive?.status === "sent" || outreachActive?.status === "replied" || outreachActive?.status === "no_reply") && outreachActive?.messages && (
                  <>
                    <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                      {outreachActive.messages.map((m: any) => (
                        <div key={m.id} className={`rounded-lg p-3 border ${m.senderRole === "investor" ? "bg-purple-500/10 border-purple-500/25" : "bg-white/[0.03] border-white/[0.08]"}`}>
                          <p className="text-[10px] uppercase tracking-wider font-mono text-white/45 mb-1">{m.senderRole === "investor" ? "Investor" : m.senderRole === "kavya" ? "Kavya (nudge)" : "You"} · {new Date(m.createdAt).toLocaleString()}</p>
                          <p className="text-white/85 text-sm whitespace-pre-wrap">{m.body}</p>
                        </div>
                      ))}
                    </div>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider font-mono text-white/45">Reply on this thread</span>
                      <textarea value={outreachBody} onChange={(e) => setOutreachBody(e.target.value)} rows={5} maxLength={4000}
                        placeholder="Write a follow-up — goes straight to the investor (no admin gate on follow-ups)."
                        className="mt-1 w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 resize-none" />
                    </label>
                    <div className="flex gap-2 mt-3">
                      <button onClick={async () => {
                        if (!outreachActive || !outreachBody.trim()) return;
                        setOutreachSending(true); setOutreachError(null);
                        const token = localStorage.getItem("banker_token");
                        try {
                          const r = await fetch(`${BASE}api/banker/outreach/${outreachActive.id}/reply`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
                            body: JSON.stringify({ body: outreachBody.trim() }),
                          });
                          if (!r.ok) { const e = await r.json().catch(() => ({})); setOutreachError(e.error || "Reply failed"); }
                          else { const j = await r.json(); setOutreachActive(j); setOutreachBody(""); await loadOutreach(); }
                        } catch { setOutreachError("Network error"); }
                        setOutreachSending(false);
                      }}
                        disabled={outreachSending || !outreachBody.trim()}
                        className="flex-1 bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
                        {outreachSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send reply</>}
                      </button>
                    </div>
                  </>
                )}

                {(!outreachActive || ["draft", "rejected"].includes(outreachActive.status)) && (
                  <>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider font-mono text-white/45">Subject</span>
                      <input value={outreachSubject} onChange={(e) => setOutreachSubject(e.target.value)} maxLength={200}
                        className="mt-1 w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white" />
                    </label>
                    <label className="block mt-3">
                      <span className="text-[10px] uppercase tracking-wider font-mono text-white/45">Message</span>
                      <textarea value={outreachBody} onChange={(e) => setOutreachBody(e.target.value)} rows={10} maxLength={8000}
                        className="mt-1 w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white resize-none font-sans leading-relaxed" />
                    </label>
                    <div className="flex items-center gap-3 mt-3">
                      <span className="text-[10px] uppercase tracking-wider font-mono text-white/45">Channel</span>
                      {(["both", "in_portal", "email"] as const).map((c) => (
                        <button key={c} onClick={() => setOutreachChannel(c)}
                          className={`px-3 py-1 rounded text-xs border ${outreachChannel === c ? "bg-[#FF5C00]/20 border-[#FF5C00]/40 text-[#FF5C00]" : "border-white/15 text-white/60"}`}>
                          {c === "both" ? "In-portal + Email" : c === "in_portal" ? "In-portal only" : "Email only"}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-5">
                      <button onClick={sendOutreach} disabled={outreachSending || !outreachSubject.trim() || !outreachBody.trim()}
                        className="flex-1 bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
                        {outreachSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send</>}
                      </button>
                      <button onClick={async () => { await saveOutreachDraft(); setOutreachOpen(false); }} disabled={outreachSending}
                        className="px-4 py-2.5 border border-white/15 rounded-lg text-sm text-white/70">Save draft</button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
